import { useEffect } from 'react';
import { Box } from '@material-ui/core';

import { TotalsDetailModule } from 'features/order/detail/shared';
import OrderDetailDialogMainSection from '../order-detail-dialog-main-section';
import OrderDetailDialogProductsSection from '../order-detail-dialog-products-section';
import { TimeoutOrderActions } from '../order-management/timeout';
import { OrderDetailDisclaimer } from '../order-management/shared';
import { CountdownToEdit } from '../order-management/components/countdown-edit';
import { OrderMgmtProducts } from '../order-management/components/order-mgmt-products/OrderMgmtProducts';
import OrderManagementActionsSection from './components/order-management-actions-section/OrderManagementActionsSection';

import {
  storeAuditAnalytics,
  storeAuditService,
} from 'features/order/store-audit/infra';
import {
  orderDetailApp,
  orderMgmtState,
  showOrderDetailTimeout,
} from '../../business/order-mgmt';
import { hooks as orderManagementHooks } from './hooks';
import { OrderMgmtAnalytics } from '../../business/order-mgmt/analitycs';
import PartnerOrderStateType from '../../constants/PartnerOrderStateType';
import { orderMgmtDetailFacade } from '../../business/order-mgmt/infra/store/order-detail';

import useStyles from './OrderDetailDialogBody.styles';

const OrderDetailDialogBody = ({
  showActionBox,
  orderIdByProp,
  orderDetail,
  editTitle,
  editDescription,
  productsText,
  orderDetailText,
  totalProducts = 0,
}) => {
  const componentClasses = useStyles();
  const {
    selectIsDisabled,
    selectProducts,
    onClearState,
    onDisabledByState,
    selectOrderState,
  } = orderMgmtDetailFacade();
  const {
    get: getOrderDetail,
    handle: handleOrderDetail,
    isTimeoutOrder,
  } = orderDetailApp();
  const isNewOrder =
    orderDetail?.partner_order_state === PartnerOrderStateType.NEWORDER;
  const {
    hasOrderManagement,
    showProductsWithOM,
    orderManagement,
    orderId,
    storeId,
    isEnabledToEdit,
  } = getOrderDetail();
  const isInEditionMode =
    selectOrderState === orderMgmtState.SendUserApproval ||
    selectOrderState === orderMgmtState.NoUserResponse;

  const showTimeoutActions = showOrderDetailTimeout(
    orderDetail?.state_order_management?.state,
    orderDetail?.partner_order_state,
  );

  const totalProductsText =
    totalProducts > 1 ? productsText : productsText.slice(0, -1);
  const orderDetailDescription = `${orderDetailText}`;
  const totalUnitsProductsText = `${totalProducts} ${totalProductsText}`;
  const _storeAudit = storeAuditService.getOrderAudit(orderId);
  const _hasDefectRate = storeAuditService.hasDefectRateReport(orderDetail);
  const isMandatory = orderDetail?.qr_information?.is_mandatory ?? false;
  const showNewOrderActions = orderManagementHooks.useShowNewOrderActions();
  const _HIDE_OM = true;

  useEffect(() => {
    if (hasOrderManagement) {
      onDisabledByState(
        orderManagement?.state,
        orderDetail?.partner_order_state,
      );
      handleOrderDetail();
    }
    return () => onClearState();
  }, []);

  useEffect(() => {
    if (!isNewOrder) return;

    if (!_HIDE_OM) {
      OrderMgmtAnalytics.whenOpenOrderStatus(
        { orderId, storeId },
        { isEnabledToEdit },
      );
    }
  }, []);

  useEffect(() => {
    if (_hasDefectRate && !_storeAudit?.hasOpeningTracking) {
      storeAuditAnalytics.log({
        orderId,
        storeId,
        isMandatory,
      });
      storeAuditService.trackingUpdate(orderId);
    }
  }, []);

  return (
    <Box className={componentClasses.container}>
      {isInEditionMode && !_HIDE_OM && (
        <CountdownToEdit title={editTitle} description={editDescription} />
      )}
      {hasOrderManagement && !_HIDE_OM && (
        <OrderDetailDisclaimer order={orderManagement} />
      )}
      <div
        className={componentClasses.root}
        data-has-new-orders={showNewOrderActions}
      >
        {selectIsDisabled && !_HIDE_OM && (
          <Box className={componentClasses.blockContent}></Box>
        )}
        {/* sidebar */}
        <OrderDetailDialogMainSection
          classes={{ root: componentClasses.mainSectionRoot }}
        />
        {/* product list */}
        <Box className={componentClasses.productsSectionRoot}>
          {showProductsWithOM && !_HIDE_OM && (
            <OrderMgmtProducts
              products={selectProducts}
              orderId={orderDetail.id}
              isTimeoutState={isTimeoutOrder}
              isEnabled={false}
              orderManagement={orderManagement}
              hasOrderManagement={hasOrderManagement}
              orderDetailDescription={orderDetailDescription}
            />
          )}

          <OrderDetailDialogProductsSection
            orderDetailDescription={orderDetailDescription}
            orderManagement={orderManagement}
            hasOrderManagement={hasOrderManagement}
            totalUnitsProductsText={totalUnitsProductsText}
          />
        </Box>

        <TotalsDetailModule orderId={orderIdByProp} />

        {showTimeoutActions ? (
          <TimeoutOrderActions />
        ) : (
          showActionBox && (
            <OrderManagementActionsSection
              classes={{ root: componentClasses.orderManagementActionsRoot }}
              isDisabled={selectIsDisabled}
            />
          )
        )}
      </div>
    </Box>
  );
};

export default OrderDetailDialogBody;
